import { Component, type ErrorInfo, type ReactNode, useEffect } from 'react';
import { MotionConfig } from 'framer-motion';
import { Navigate, Route, Routes, useLocation, useNavigate } from 'react-router-dom';
import { Shell } from './components/Shell';
import { AdminPage } from './pages/AdminPage';
import { JoinPage } from './pages/JoinPage';
import { LookupPage } from './pages/LookupPage';
import { OverviewPage } from './pages/OverviewPage';
import { PartnersPage, StatisticsPage, TeamPage, TransactionsPage } from './pages/NetworkPages';
import { RoyalPage } from './pages/RoyalPage';
import { SlotDetailPage } from './pages/SlotDetailPage';
import { SlotsPage } from './pages/SlotsPage';
import { BenefitsPage, TokensPage } from './pages/UtilityPages';
import { AppDataProvider } from './state/AppDataContext';

class AppErrorBoundary extends Component<{ children: ReactNode }, { failed: boolean }> {
  override state = { failed: false };

  static getDerivedStateFromError() { return { failed: true }; }

  override componentDidCatch(error: Error, info: ErrorInfo) {
    if (import.meta.env.DEV) console.error('Application boundary caught an error', error, info);
  }

  override render() {
    if (this.state.failed) {
      return <div className="fatal-error"><span>B·D</span><h1>The interface hit an unexpected state.</h1><p>No transaction was submitted. Reload the verified application and try again.</p><button className="button primary" type="button" onClick={() => window.location.reload()}>Reload safely</button></div>;
    }
    return this.props.children;
  }
}

function RouteEffects() {
  const location = useLocation();
  const navigate = useNavigate();
  useEffect(() => {
    const reducedMotion = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches ?? false;
    window.scrollTo({ top: 0, behavior: reducedMotion ? 'auto' : 'smooth' });
  }, [location.pathname]);
  useEffect(() => {
    const handler = (event: KeyboardEvent) => {
      if (event.key !== '/' || event.metaKey || event.ctrlKey || event.altKey) return;
      const target = event.target as HTMLElement | null;
      if (target?.matches('input, textarea, [contenteditable="true"]')) return;
      event.preventDefault();
      navigate('/lookup');
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [navigate]);
  return null;
}

function NotFoundPage() {
  return <section className="neutral-not-found"><span>404</span><h1>Page not found</h1><p>The requested resource does not exist.</p><a className="button secondary" href="/">Return to overview</a></section>;
}

export function App() {
  const adminSlug = import.meta.env.VITE_ADMIN_ROUTE_SLUG ?? 'operations-7f3c9a';
  return (
    <AppDataProvider>
      <AppErrorBoundary>
        <MotionConfig reducedMotion="user">
          <Shell>
          <RouteEffects />
          <Routes>
            <Route path="/" element={<OverviewPage />} />
            <Route path="/join" element={<JoinPage />} />
            <Route path="/slots" element={<SlotsPage />} />
            <Route path="/slots/:slotId" element={<SlotDetailPage />} />
            <Route path="/partners" element={<PartnersPage />} />
            <Route path="/team" element={<TeamPage />} />
            <Route path="/transactions" element={<TransactionsPage />} />
            <Route path="/statistics" element={<StatisticsPage />} />
            <Route path="/tokens" element={<TokensPage />} />
            <Route path="/benefits" element={<BenefitsPage />} />
            <Route path="/royal" element={<RoyalPage />} />
            <Route path="/lookup" element={<LookupPage />} />
            <Route path={`/${adminSlug}`} element={<AdminPage />} />
            <Route path="/dashboard" element={<Navigate to="/" replace />} />
            <Route path="*" element={<NotFoundPage />} />
          </Routes>
          </Shell>
        </MotionConfig>
      </AppErrorBoundary>
    </AppDataProvider>
  );
}
